import { useEffect, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSort } from '../../../hooks/useSort';
import { usePaginationState } from '../../../hooks/usePaginationState';
import { useSettingsStore } from '../../../store/settingsStore';
import { groupItemsByDate } from '../../../utils/date';
import { purchaseOrderService } from '../../../services/purchaseOrderService';
import { supplierService } from '../../../services/supplierService';
import { greenBeanService } from '../../../services/greenBeanService';
import { inventoryService } from '../../../services/inventoryService';
import type { PurchaseOrder, PurchaseOrderFormData, PurchaseOrderItem } from '../../../types/purchaseOrder';
import type { Supplier } from '../../../types/supplier';
import type { GreenBeanProfile } from '../../../types/greenBean';

interface LocationState {
  createPO?: boolean;
  initialItem?: {
    inventoryItemId: string;
    quantity: number;
    unitPrice: number;
    name: string;
  };
}

export interface ProfileModalData {
  isOpen: boolean;
  item?: PurchaseOrderItem;
  existingProfile?: GreenBeanProfile;
  templateProfile?: Partial<GreenBeanProfile>;
}

export const usePurchaseOrderListState = () => {
  const queryClient = useQueryClient();
  const { currencySymbol } = useSettingsStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderStartDateFilter, setOrderStartDateFilter] = useState('');
  const [orderEndDateFilter, setOrderEndDateFilter] = useState('');
  const [deliveryStartDateFilter, setDeliveryStartDateFilter] = useState('');
  const [deliveryEndDateFilter, setDeliveryEndDateFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<string>('all');
  const [viewingSupplier, setViewingSupplier] = useState<Supplier | null>(null);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [receivingPO, setReceivingPO] = useState<PurchaseOrder | null>(null);
  const [payingPO, setPayingPO] = useState<PurchaseOrder | null>(null);
  const [profileModalData, setProfileModalData] = useState<ProfileModalData>({ isOpen: false });

  const { data: greenBeanProfiles = [] } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
  });

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([
    searchFilter,
    statusFilter,
    orderStartDateFilter,
    orderEndDateFilter,
    deliveryStartDateFilter,
    deliveryEndDateFilter,
    paymentStatusFilter,
    deliveryStatusFilter,
  ]);

  const { data: posResponse, isLoading: loading, error: queryError } = useQuery({
    queryKey: [
      'purchase-orders',
      {
        page,
        size,
        search: searchFilter,
        status: statusFilter,
        paymentStatus: paymentStatusFilter,
        deliveryStatus: deliveryStatusFilter,
        orderStartDate: orderStartDateFilter,
        orderEndDate: orderEndDateFilter,
        deliveryStartDate: deliveryStartDateFilter,
        deliveryEndDate: deliveryEndDateFilter,
      },
    ],
    queryFn: () =>
      purchaseOrderService.list({
        page,
        size,
        search: searchFilter,
        status: statusFilter,
        paymentStatus: paymentStatusFilter,
        deliveryStatus: deliveryStatusFilter,
        orderStartDate: orderStartDateFilter,
        orderEndDate: orderEndDateFilter,
        deliveryStartDate: deliveryStartDateFilter,
        deliveryEndDate: deliveryEndDateFilter,
      }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const pos = posResponse?.data ?? [];
  const totalPOs = posResponse?.total ?? 0;

  const error = queryError ? 'Failed to load purchase orders. Please try again.' : null;

  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.createPO) {
      navigate('/purchase-orders/create', { state });
    }
  }, [location, navigate]);

  const handleProfileClick = async (item: PurchaseOrderItem) => {
    const existingProfile = greenBeanProfiles.find(profile => profile.lot_id === item.lotId);

    if (existingProfile) {
      setProfileModalData({
        isOpen: true,
        item,
        existingProfile,
      });
      return;
    }

    let templateProfile: Partial<GreenBeanProfile> | undefined;

    if (item.inventoryItemId) {
      try {
        const inventoryItem = await inventoryService.getById(item.inventoryItemId);
        if (inventoryItem.greenBeanProfiles && inventoryItem.greenBeanProfiles.length > 0) {
          const defaultProfile = inventoryItem.greenBeanProfiles.find(profile => profile.is_default_profile);
          if (defaultProfile) {
            templateProfile = { ...defaultProfile };
          } else {
            const sorted = [...inventoryItem.greenBeanProfiles].sort((a, b) => {
              const dateA = new Date(a.created_at || 0).getTime();
              const dateB = new Date(b.created_at || 0).getTime();
              return dateB - dateA;
            });
            if (sorted.length > 0) {
              templateProfile = { ...sorted[0] };
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch inventory item for profile template', err);
      }
    }

    setProfileModalData({
      isOpen: true,
      item,
      existingProfile: undefined,
      templateProfile,
    });
  };

  const handleUpdatePO = async (data: PurchaseOrderFormData) => {
    if (!editingPO) return;
    try {
      await purchaseOrderService.update(editingPO.id, data, editingPO.orderNumber);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
        queryClient.invalidateQueries({ queryKey: ['lots'] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] }),
      ]);
    } catch (err) {
      console.error('Failed to update PO:', err);
      throw err;
    }
  };

  const handleSupplierClick = async (supplierId: string) => {
    if (!supplierId) return;
    try {
      const supplier = await supplierService.getById(supplierId);
      if (supplier) {
        setViewingSupplier(supplier);
        setIsSupplierModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load supplier details:', err);
    }
  };

  // Server handles all filtering. Client-side sort only affects current page.
  const { sortedItems: filteredPOs, sortConfig, handleSort } = useSort(pos);

  const shouldGroup = !sortConfig || sortConfig.key === 'orderDate';
  const groupedPOs = shouldGroup ? groupItemsByDate(filteredPOs, po => po.orderDate) : {};
  const sortedDates = shouldGroup ? Object.keys(groupedPOs).sort((a, b) => b.localeCompare(a)) : [];

  const toggleRow = (id: string, event: MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedRowIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRowIds(newExpanded);
  };

  const handleReceiveSubmit = async (data: PurchaseOrderFormData) => {
    if (!receivingPO) return;
    await purchaseOrderService.update(receivingPO.id, data, receivingPO.orderNumber);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
      queryClient.invalidateQueries({ queryKey: ['lots'] }),
      queryClient.invalidateQueries({ queryKey: ['samples'] }),
    ]);
    setReceivingPO(null);
  };

  const handlePaySubmit = async (data: PurchaseOrderFormData) => {
    if (!payingPO) return;
    await purchaseOrderService.update(payingPO.id, data, payingPO.orderNumber);
    await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    setPayingPO(null);
  };

  return {
    currencySymbol,
    navigate,
    loading,
    error,
    pos,
    editingPO,
    setEditingPO,
    searchFilter,
    setSearchFilter,
    statusFilter,
    setStatusFilter,
    orderStartDateFilter,
    setOrderStartDateFilter,
    orderEndDateFilter,
    setOrderEndDateFilter,
    deliveryStartDateFilter,
    setDeliveryStartDateFilter,
    deliveryEndDateFilter,
    setDeliveryEndDateFilter,
    paymentStatusFilter,
    setPaymentStatusFilter,
    deliveryStatusFilter,
    setDeliveryStatusFilter,
    viewingSupplier,
    setViewingSupplier,
    isSupplierModalOpen,
    setIsSupplierModalOpen,
    expandedRowIds,
    toggleRow,
    receivingPO,
    setReceivingPO,
    payingPO,
    setPayingPO,
    profileModalData,
    setProfileModalData,
    greenBeanProfiles,
    handleProfileClick,
    handleUpdatePO,
    handleSupplierClick,
    filteredPOs,
    totalPOs,
    page,
    pageSize: size,
    setPage,
    setPageSize: setSize,
    sortConfig,
    handleSort,
    shouldGroup,
    groupedPOs,
    sortedDates,
    handleReceiveSubmit,
    handlePaySubmit,
  };
};
